import { paths } from '@/config/paths'
import { Panel } from '@/ui/atoms/panel/Panel'
import { Skeleton } from '@/ui/atoms/skeleton/Skeleton'
import { Typography } from '@/ui/atoms/typography/Typography'
import { PageLayout } from '@/ui/layouts/PageLayout'
import Link from 'next/link'
import { Suspense } from 'react'
import { PageHero } from '@/ui/molecules/page-hero/PageHero'
import { PageHeroStats } from '@/ui/molecules/page-hero/PageHeroStats'
import { UsdxlHeroStats } from './components/UsdxlHeroStats'
import { BorrowUsdxl } from './components/BorrowUsdxl'
import { BuyUsdxl } from './components/BuyUsdxl'
import { UsdxlVaults } from './components/Vaults'
import { GsmMintContainer } from './components/form/GsmMintContainer'
import { GsmBurnContainer } from './components/form/GsmBurnContainer'
import { HistoricalInterest } from './components/historical-rate/HistoricalInterest'
import { SummaryTiles } from './components/tiles/SummaryTiles'
import { SectionHeader } from '@/ui/molecules/section-header/SectionHeader'
import { Sparkles } from 'lucide-react'

function UsdxlContent() {
  return (
    <>
      {/* Key Stats */}
      <SummaryTiles />

      {/* Redeem USDXL */}
      <div className="flex flex-col gap-6">
        <SectionHeader
          icon={<Sparkles className="h-4 w-4 text-[#FBE572]" />}
          title="Redeem USDXL"
          description="Burn USDXL to redeem USDT0 from the GSM at the current rate, subject to available liquidity."
        />
        <GsmBurnContainer />
      </div>

      {/* Get USDXL */}
      <div className="flex flex-col gap-6">
        <SectionHeader
          icon={<Sparkles className="h-4 w-4 text-[#FBE572]" />}
          title="Get USDXL"
          description={
            <Typography variant="p" className="text-muted-foreground text-xs leading-relaxed sm:text-sm">
              Buy USDXL through HyperEVM DEXs on{' '}
              <Link href={paths.swap} className="text-primary underline-offset-2 hover:underline">
                HypurrFi Swap
              </Link>
              , borrow it from{' '}
              <Link href={paths.earn} className="text-primary underline-offset-2 hover:underline">
                HypurrFi Pooled
              </Link>{' '}
              against your collateral, or mint it 1:1 with stablecoins. You can also buy it on Hyperliquid via the{' '}
              <a
                href="https://app.hyperliquid.xyz/trade/USDXL/USDC"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary underline-offset-2 hover:underline"
              >
                USDXL/USDC
              </a>{' '}
              and{' '}
              <a
                href="https://app.hyperliquid.xyz/trade/USDXL/USDT0"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary underline-offset-2 hover:underline"
              >
                USDXL/USDT0
              </a>{' '}
              pairs.
            </Typography>
          }
        />
        <BorrowUsdxl />
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <BuyUsdxl />
          <GsmMintContainer />
        </div>
      </div>

      {/* Interest Rate History */}
      <HistoricalInterest />

      {/* Deprecated Vaults */}
      <UsdxlVaults />
    </>
  )
}

function UsdxlContentSkeleton() {
  return (
    <>
      {/* Stats */}
      <div className="grid grid-cols-2 items-start gap-4 py-6 sm:flex sm:justify-between">
        <Skeleton className="h-14 w-28" />
        <Skeleton className="h-14 w-28" />
        <Skeleton className="h-14 w-28" />
        <Skeleton className="h-14 w-28" />
      </div>

      {/* Borrow section */}
      <div className="flex flex-col gap-4">
        <Skeleton className="h-6 w-32" />
        <Panel.Wrapper className="p-8">
          <Skeleton className="h-48 w-full rounded-lg" />
        </Panel.Wrapper>
      </div>

      {/* Mint section */}
      <Panel.Wrapper className="p-8">
        <Skeleton className="h-64 w-full rounded-lg" />
      </Panel.Wrapper>

      {/* How it works */}
      <div className="flex flex-col gap-4">
        <Skeleton className="h-6 w-40" />
        <div className="grid gap-4 sm:grid-cols-3">
          {Array.from({ length: 3 }).map((_, index) => (
            <Panel.Wrapper key={index} className="p-5">
              <Skeleton className="h-20 w-full rounded-lg" />
            </Panel.Wrapper>
          ))}
        </div>
      </div>

      {/* Earn section */}
      <div className="flex flex-col gap-4">
        <Skeleton className="h-6 w-36" />
        <Panel.Wrapper className="p-8">
          <Skeleton className="h-32 w-full rounded-lg" />
        </Panel.Wrapper>
      </div>

      {/* Historical rate */}
      <Panel.Wrapper className="p-8">
        <Skeleton className="h-72 w-full rounded-lg" />
      </Panel.Wrapper>
    </>
  )
}

export function UsdxlContainer() {
  return (
    <PageLayout className="max-w-6xl gap-12 px-3">
      <PageHero
        title="USDXL"
        subtitle="The Hyperliquid native synthetic dollar. Borrow USDXL against your crypto collateral in HypurrFi Pooled, mint 1:1 with stablecoins."
        journey={['Mint', 'Supply', '💰 Earn yield']}
        className="mb-0"
        statsSlot={
          <Suspense
            fallback={
              <PageHeroStats
                stats={[
                  { label: 'Total Supply', value: '-' },
                  { label: 'Reserve', value: '-' },
                ]}
                loading
              />
            }
          >
            <UsdxlHeroStats />
          </Suspense>
        }
      />

      {/* Data-dependent content - suspends until blockchain data loads */}
      <Suspense fallback={<UsdxlContentSkeleton />}>
        <UsdxlContent />
      </Suspense>
    </PageLayout>
  )
}
